// === INBOEDEL — Infofolio value mappers ===
// Pure mappers extracted verbatim from InboedelScreen ("=== INFOFOLIO MAPPING FUNCTIONS ===").
// DO NOT merge with woonhuis/infofolio.ts: the code sets deliberately differ — Inboedel's
// type-woning codes are ['2','6','A','H','T','U'] (apartments keep 'A'; the 'A'->'U' rule is
// Woonhuis-only), wall codes are ['S','H','Y'] vs Woonhuis's 40+ list, and the roof mapper
// accepts 'R' as a valid code. Unknown values map to null so the field stays empty for the user.

export const mapTypeWoning = (value: string | undefined): string | null => {
  if (!value) return null;
  // Check if it's already a valid code
  if (['2', '6', 'A', 'H', 'T', 'U'].includes(value)) return value;
  // Map text values to codes
  const mappings: Record<string, string> = {
    'vrijstaand': '2', 'vrijstaande woning': '2', 'detached': '2',
    'maisonnette': '6', 'maisonette': '6',
    'appartement': 'A', 'flat': 'A', 'bovenwoning': 'A', 'benedenwoning': 'A',
    'hoekwoning': 'H', 'corner': 'H', 'hoek': 'H',
    'twee onder een kap': 'T', '2-onder-1-kap': 'T', '2 onder 1 kap': 'T',
    '2 onder 1 kap woning': 'T', 'twee-onder-een-kap': 'T', 'semi-detached': 'T',
    'tussenwoning': 'U', 'rijtjeshuis': 'U', 'terraced': 'U', 'rijwoning': 'U', 'tussen/rijwoning': 'U',
  };
  const lowerValue = value.toLowerCase();
  // Return null for unknown values - let user select
  return mappings[lowerValue] || null;
};

export const mapAardBouw = (value: string | undefined): string | null => {
  if (!value) return null;
  // Check if it's already a valid code
  if (['S', 'H', 'Y'].includes(value)) return value;
  // Map text values to codes
  const mappings: Record<string, string> = {
    'steen': 'S', 'baksteen': 'S', 'metselwerk': 'S', 'brick': 'S', 'stone': 'S',
    'hout': 'H', 'houtskelet': 'H', 'wood': 'H', 'timber': 'H',
    'steenachtig': 'Y', 'beton': 'Y', 'concrete': 'Y', 'volledig steen': 'Y',
  };
  const lowerValue = value.toLowerCase();
  // Return null for unknown values - let user select
  return mappings[lowerValue] || null;
};

/**
 * Map roof type from Infofolio to AFD BA_AARDDAK codes
 * AFD codes: H = Hard (pannen/bitumen/etc), R = Riet, K = Kunststof riet
 *
 * Infofolio provides:
 * - dakbedekking: roof covering material (pannen, bitumen, riet, etc.)
 * - dakconstructie: roof structure (Schuin=sloped, Plat=flat, etc.)
 * - dakbedekking_code / dakconstructie_code: numeric codes
 *
 * Logic:
 * 1. If dakbedekking is available, use it (most specific)
 * 2. If only dakconstructie is available, infer from structure type
 * 3. Sloped roofs (Schuin) typically have hard covering (tiles)
 * 4. Flat roofs (Plat) typically have bitumen (also hard)
 */
export const mapAardDak = (
  value: string | undefined,
  dakconstructie?: string | null,
  dakbedekking?: string | null,
  dakbedekkingCode?: string | null
): string | null => {
  // Check if it's already a valid code
  if (value && ['H', 'R', 'K'].includes(value)) return value;

  // Code-based mapping for dakbedekking_code (most reliable)
  // Common Infofolio codes:
  // 10-50 = Hard (pannen, leisteen, bitumen, etc.)
  // 60 = Riet (natural thatch)
  // 70 = Kunstriet (artificial thatch)
  if (dakbedekkingCode) {
    const code = parseInt(dakbedekkingCode, 10);
    if (!isNaN(code)) {
      if (code >= 10 && code < 60) return 'H'; // Hard coverings
      if (code === 60) return 'K'; // Riet -> Kunstriet (product standard only supports K)
      if (code === 70) return 'K'; // Kunstriet (artificial thatch)
    }
  }

  // Mapping for dakbedekking (roof covering) - most specific
  const bedekkingMappings: Record<string, string> = {
    'pannen': 'H', 'dakpannen': 'H', 'keramische pannen': 'H', 'betonpannen': 'H',
    'leisteen': 'H', 'leien': 'H', 'slate': 'H',
    'bitumen': 'H', 'bitumineus': 'H', 'dakleer': 'H',
    'kunststof': 'H', 'pvc': 'H', 'epdm': 'H',
    'metaal': 'H', 'zink': 'H', 'koper': 'H', 'staal': 'H',
    'riet': 'K', 'rieten': 'K', 'stro': 'K', 'thatched': 'K', 'reed': 'K',
    'kunststof riet': 'K', 'synthetisch riet': 'K', 'nep riet': 'K', 'kunstriet': 'K',
    'kunst riet': 'K', 'kunstmatig riet': 'K', 'imitatie riet': 'K',
  };

  // Check dakbedekking first (most accurate)
  if (dakbedekking) {
    const lowerBedekking = dakbedekking.toLowerCase();
    if (bedekkingMappings[lowerBedekking]) {
      return bedekkingMappings[lowerBedekking];
    }
  }

  // Mapping for dakconstructie (roof structure) - fallback inference
  const constructieMappings: Record<string, string> = {
    // Sloped roofs typically have hard covering (tiles)
    'schuin': 'H', 'hellend': 'H', 'zadeldak': 'H', 'mansardedak': 'H',
    'schilddak': 'H', 'lessenaarsdak': 'H', 'puntdak': 'H',
    // Flat roofs typically have bitumen (also hard)
    'plat': 'H', 'platdak': 'H', 'flat': 'H',
  };

  // Check dakconstructie as fallback
  if (dakconstructie) {
    const lowerConstructie = dakconstructie.toLowerCase();
    if (constructieMappings[lowerConstructie]) {
      return constructieMappings[lowerConstructie];
    }
  }

  // Original value mapping (backwards compatibility)
  if (value) {
    const valueMappings: Record<string, string> = {
      'hard': 'H', 'pannen': 'H', 'dakpannen': 'H', 'tiles': 'H', 'hard dak': 'H',
      'leisteen': 'H', 'bitumen': 'H', 'kunststof': 'H',
      'riet': 'K', 'rieten dak': 'K', 'stro': 'K', 'thatched': 'K', 'reed': 'K',
      'kunststof riet': 'K', 'synthetisch riet': 'K', 'nep riet': 'K', 'synthetic reed': 'K',
    };
    const lowerValue = value.toLowerCase();
    if (valueMappings[lowerValue]) {
      return valueMappings[lowerValue];
    }
  }

  // Return null for unknown values - let user select
  return null;
};
